import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CreditCard, Users, LogOut,
  Shield, ChevronRight, Building2, KeyRound, X, Eye, EyeOff, Save, Loader2, Monitor
} from "lucide-react";
import { useState as useStateLocal } from "react";
import adminApi, { ADMIN_API } from "@/pages/admin/adminApi";

const NAV_ITEMS = [
  { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard",  roles: ["admin", "tesoreria", "consulta"] },
  { to: "/admin/pagos",     icon: CreditCard,       label: "Pagos",      roles: ["admin", "tesoreria", "consulta"] },
  { to: "/admin/usuarios",  icon: Users,            label: "Usuarios",   roles: ["admin"] },
  { to: "/admin/cajas",     icon: Monitor,          label: "Cajas POS",  roles: ["admin"] },
];


// ─── Modal cambio de clave propio (disponible para todos los roles) ───────────
const ModalCambioClaveLayout = ({ usuario, onClose }) => {
  const [form, setForm] = useStateLocal({ password_actual: "", password_nuevo: "", password_confirm: "" });
  const [showPass, setShowPass] = useStateLocal({ actual: false, nuevo: false, confirm: false });
  const [guardando, setGuardando] = useStateLocal(false);
  const [error, setError] = useStateLocal("");
  const [exito, setExito] = useStateLocal(false);

  const handleGuardar = async () => {
    if (!form.password_actual || !form.password_nuevo || !form.password_confirm) {
      setError("Completá todos los campos"); return;
    }
    if (form.password_nuevo.length < 6) {
      setError("La nueva contraseña debe tener al menos 6 caracteres"); return;
    }
    if (form.password_nuevo !== form.password_confirm) {
      setError("Las contraseñas nuevas no coinciden"); return;
    }
    setGuardando(true);
    setError("");
    try {
      await adminApi.post(`${ADMIN_API}/usuarios/cambiar-clave`, {
        password_actual: form.password_actual,
        password_nuevo: form.password_nuevo,
      });
      setExito(true);
      setTimeout(onClose, 2000);
    } catch (e) {
      setError(e.response?.data?.detail || "Contraseña actual incorrecta");
    } finally {
      setGuardando(false);
    }
  };

  const Toggle = ({ campo }) => (
    <button type="button"
      onClick={() => setShowPass(s => ({ ...s, [campo]: !s[campo] }))}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
      {showPass[campo] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="font-bold text-slate-800">Cambiar contraseña</h2>
            <p className="text-xs text-slate-400 mt-0.5">{usuario?.username}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {exito ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Save className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-green-700 font-semibold text-center">Contraseña actualizada correctamente</p>
            </div>
          ) : (
            <>
              {[
                { campo: "actual",  label: "Contraseña actual",          key: "password_actual" },
                { campo: "nuevo",   label: "Nueva contraseña",           key: "password_nuevo" },
                { campo: "confirm", label: "Confirmar nueva contraseña",  key: "password_confirm" },
              ].map(({ campo, label, key }) => (
                <div key={key}>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">{label}</label>
                  <div className="relative">
                    <input
                      type={showPass[campo] ? "text" : "password"}
                      value={form[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      placeholder="••••••••"
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-blue-500"
                    />
                    <Toggle campo={campo} />
                  </div>
                </div>
              ))}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-600">{error}</div>
              )}
              <button
                onClick={handleGuardar}
                disabled={guardando}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {guardando
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Guardando...</>
                  : <><Save className="w-4 h-4" />Actualizar contraseña</>
                }
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminLayout = ({ children, usuario, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [modalClave, setModalClave] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    onLogout();
    navigate("/admin/login");
  };

  const itemsVisibles = NAV_ITEMS.filter(item => item.roles.includes(usuario?.rol));

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? "w-64" : "w-16"} bg-slate-900 flex flex-col transition-all duration-300 flex-shrink-0`}>
        {/* Header Sidebar — el ícono del escudo actúa como toggle */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-slate-700/50">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-8 h-8 bg-blue-600 hover:bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
            title={sidebarOpen ? "Contraer menú" : "Expandir menú"}
          >
            <Shield className="w-4 h-4 text-white" />
          </button>
          {sidebarOpen && (
            <div className="overflow-hidden">
              <p className="text-white font-bold text-sm truncate">Panel Admin</p>
              <p className="text-slate-400 text-xs truncate">Muni. Pocito</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-1">
          {itemsVisibles.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-medium ${
                  isActive
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                }`
              }
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Usuario + Logout */}
        <div className="px-2 py-4 border-t border-slate-700/50">
          {sidebarOpen && usuario && (
            <div className="px-3 py-2 mb-2">
              <p className="text-white text-sm font-medium truncate">{usuario.nombre_completo}</p>
              <p className="text-slate-400 text-xs">{usuario.rol_label}</p>
            </div>
          )}
          {/* Cambiar contraseña — visible para todos los roles */}
          <button
            onClick={() => setModalClave(true)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl w-full text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all text-sm font-medium mb-1"
            title="Cambiar mi contraseña"
          >
            <KeyRound className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Mi contraseña</span>}
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl w-full text-slate-400 hover:text-white hover:bg-red-500/20 transition-all text-sm font-medium"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        {/* Top bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-2 text-sm text-slate-500">
          <Building2 className="w-4 h-4" />
          <span>Municipalidad de Pocito</span>
          <ChevronRight className="w-3 h-3" />
          <span className="text-slate-800 font-medium">Panel Administrativo</span>
        </div>

        <div className="p-6">
          {children}
        </div>
      </main>

      {/* Modal cambio de clave — disponible para todos los roles */}
      {modalClave && (
        <ModalCambioClaveLayout
          usuario={usuario}
          onClose={() => setModalClave(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
